import { PencilIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import { BookingSystem as BookingSystemType } from '../../../apis/types';
import { useToast } from '../../../context/ToastContext';
import { useUser } from '../../../hooks/useUser';
import { getBookingSystemDisplayName } from '../utils/bookingSystemUtils';

const BookingSystem: React.FC = () => {
  const { user, loading, updating, updateUser } = useUser();
  const { showToast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<BookingSystemType>('datev');

  useEffect(() => {
    if (user?.bookingSystem) {
      setSelectedSystem(user.bookingSystem);
    }
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateUser({ bookingSystem: selectedSystem });
      showToast(
        'Booking system updated',
        'Your booking system has been updated successfully.',
        'success'
      );
      setIsEditing(false);
    } catch (err) {
      showToast(
        'Failed to update booking system',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleCancel = () => {
    setSelectedSystem(user?.bookingSystem ?? 'datev');
    setIsEditing(false);
  };

  const currentSystem = user?.bookingSystem ?? 'datev';
  const hasChanged = selectedSystem !== currentSystem;

  if (loading) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='animate-pulse'>
          <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4'></div>
          <div className='h-20 bg-gray-200 dark:bg-gray-700 rounded'></div>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='flex justify-between items-start mb-4 min-h-[40px]'>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
            Booking System
          </h2>
        </div>

        <form onSubmit={handleSave} className='space-y-3'>
          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-3'>
              Select booking system
            </span>
            <div className='flex gap-6'>
              <label className='flex items-center space-x-2 cursor-pointer'>
                <input
                  type='radio'
                  name='bookingSystem'
                  value='datev'
                  checked={selectedSystem === 'datev'}
                  onChange={e =>
                    setSelectedSystem(e.target.value as BookingSystemType)
                  }
                  className='w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2'
                />
                <span className='text-gray-900 dark:text-white'>DATEV</span>
              </label>
              <label className='flex items-center space-x-2 cursor-pointer'>
                <input
                  type='radio'
                  name='bookingSystem'
                  value='lexoffice'
                  checked={selectedSystem === 'lexoffice'}
                  onChange={e =>
                    setSelectedSystem(e.target.value as BookingSystemType)
                  }
                  className='w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2'
                />
                <span className='text-gray-900 dark:text-white'>
                  Lexware Office
                </span>
              </label>
            </div>
          </div>

          <div className='flex justify-end gap-2 pt-1'>
            <button
              type='button'
              onClick={handleCancel}
              disabled={updating}
              className='px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={updating || !hasChanged}
              className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
      <div className='flex justify-between items-start mb-4 min-h-[40px]'>
        <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
          Booking System
        </h2>
        <button
          onClick={() => setIsEditing(true)}
          className='p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-md transition-colors'
          aria-label='Edit booking system'
        >
          <PencilIcon className='w-5 h-5' />
        </button>
      </div>

      <div className='space-y-3'>
        <div className='pb-3'>
          <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
            Current System
          </span>
          <p className='text-gray-900 dark:text-white font-medium text-lg'>
            {getBookingSystemDisplayName(currentSystem)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookingSystem;
